import { useRef } from 'react';
import { NSBoxEnum, NSBoxInteger, NSBoxString, NSColumn, NSRow } from 'namirasoft-site-react';
import { NSARouterMakerResult } from 'namirasoft-account-react';
import { LogGroupInputRow, LogGroupRetentionUnit, LogGroupRow } from 'namirasoft-log';
import { LogLayout } from '../LogLayout';
import { LogEntities } from '../LogEntities';
import { LogProps } from '../LogProps';
import { NSCCostChart } from 'namirasoft-cost-react';

interface LogGroupEditPageProps extends NSARouterMakerResult, LogProps
{
	isEdit: boolean;
}

function LogGroupEditPage(props: LogGroupEditPageProps) 
{
	const NSBoxString_Name = useRef<NSBoxString>(null);
	const NSBoxEnum_Unit = useRef<NSBoxEnum<LogGroupRetentionUnit>>(null);
	const NSBoxInteger_Value = useRef<NSBoxInteger>(null);

	return (
		<LogLayout<LogGroupRow, LogGroupInputRow>
			{...props}
			action={{
				description: props.isEdit ? "This section allows you to update the configuration of an existing log group, including its name, retention rules, or other settings." : "This section allows you to create a new Log Group, which acts as a container for incoming logs. Each Log Group defines how logs are organized, stored, and analyzed across applications or environments.",
			}}
			pages={{
				entity: LogEntities.log_group(props),
				edit: {
					...props,
					ui: {
						tab_page_edit: {
							editor: {
								getInputRow: () =>
								{
									let name: string = NSBoxString_Name.current?.getValue() ?? "";
									let retention_unit: LogGroupRetentionUnit = NSBoxEnum_Unit.current?.getValueEnumOne(LogGroupRetentionUnit, LogGroupRetentionUnit.Day) ?? LogGroupRetentionUnit.Day;
									let retention_value: number = NSBoxInteger_Value.current?.getValue() ?? 0;
									let workspace_id = null;
									return {
										name,
										retention_unit,
										retention_value,
										workspace_id,
										log_group_category: [],
										log_group_field: [],
										log_group_tag: []
									}
								},
								setRow: (row) =>
								{
									NSBoxString_Name.current?.setValue(row.name);
									NSBoxEnum_Unit.current?.setValue(row.retention_unit);
									NSBoxInteger_Value.current?.setValue(row.retention_value);
								}
							}
						},
						tab_page_view: {
							tabs: {
								extra: {
									after_history: [
										NSCCostChart.getTab(props)
									]
								}
							}
						}
					}
				}
			}}
		>
			<NSColumn>
				<NSRow>
					<NSBoxString
						ref={NSBoxString_Name}
						title="Name"
						required
						style={{ width: "100%" }}
						info={{ link: "https://namirasoft.com/log/console-guide/log-group/#field-name" }}
					/>
				</NSRow>
				<NSRow>
					<NSBoxEnum<LogGroupRetentionUnit>
						ref={NSBoxEnum_Unit}
						title='Retention Unit'
						required
						multiple={false}
						getEnumObject={() => LogGroupRetentionUnit}
						defaultValue={LogGroupRetentionUnit.Day}
						info={{ link: "https://namirasoft.com/log/console-guide/log-group/#field-retention-unit" }}
					/>
					<NSBoxInteger
						ref={NSBoxInteger_Value}
						title='Retention Value'
						required
						min={0}
						info={{ link: "https://namirasoft.com/log/console-guide/log-group/#field-retention-value" }}
					/>
				</NSRow>
			</NSColumn>
		</LogLayout >
	);
}

export default LogGroupEditPage;